/**
 * fileDropClassifier — pure routing decisions for the document-level file-drop
 * router (`useFileDrop`). No DOM, no stores — unit-testable in isolation.
 */

export type DroppedFileKind = 'sql' | 'import' | 'unsupported';

const IMPORT_EXTENSIONS = ['csv', 'tsv', 'txt', 'json', 'ndjson', 'xlsx'] as const;

/** Classify a dropped file by extension. `.txt` → import (SQL files keep `.sql`). */
export function classifyDroppedFileName(name: string): DroppedFileKind {
  const lower = name.toLowerCase();
  if (lower.endsWith('.sql')) return 'sql';
  if (IMPORT_EXTENSIONS.some((ext) => lower.endsWith(`.${ext}`))) return 'import';
  return 'unsupported';
}

export type DropAction =
  | { kind: 'import'; index: number } // open the wizard on files[index]
  | { kind: 'sql'; indices: number[]; unsupportedNames: string[] } // open each .sql (existing behavior)
  | { kind: 'unsupported' }; // nothing actionable

/** Decide what a multi-file drop does. Precedence: ANY import file present →
 *  import wins, FIRST import file only (rest ignored). Else all `.sql` files
 *  (dropping the names of any unsupported files alongside them, so the caller
 *  can still warn about them). Else unsupported. Returns the ignored-file
 *  count for the caller's toast. */
export function decideDropAction(names: string[]): { action: DropAction; ignoredCount: number } {
  const kinds = names.map(classifyDroppedFileName);
  const firstImport = kinds.indexOf('import');
  if (firstImport !== -1) {
    return { action: { kind: 'import', index: firstImport }, ignoredCount: names.length - 1 };
  }
  const sqlIndices = kinds.flatMap((k, i) => (k === 'sql' ? [i] : []));
  if (sqlIndices.length > 0) {
    const unsupportedNames = names.filter((_, i) => kinds[i] === 'unsupported');
    return { action: { kind: 'sql', indices: sqlIndices, unsupportedNames }, ignoredCount: 0 };
  }
  return { action: { kind: 'unsupported' }, ignoredCount: 0 };
}

const IMPORT_MIME_TYPES = new Set([
  'text/csv',
  'application/json',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/tab-separated-values',
]);

/** Best-effort drag-time hint from MIME types (filenames are NOT available
 *  before drop by web-platform design). Returns 'import' only when an import
 *  MIME is positively present; otherwise 'default'. */
export function classifyDragPayload(types: readonly string[]): 'import' | 'default' {
  return types.some((t) => IMPORT_MIME_TYPES.has(t)) ? 'import' : 'default';
}
